import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface TenantInfo {
  id: string;
  name: string;
  identifier: string;
  logoUrl?: string;
  primaryColor?: string;
  features: string[];
}

interface TenantState {
  // State
  tenantId: string | null;
  tenantInfo: TenantInfo | null;

  // Actions
  setTenant: (info: TenantInfo) => void;
  clearTenant: () => void;

  // Feature flags
  hasFeature: (feature: string) => boolean;
}

export const useTenantStore = create<TenantState>()(
  persist(
    (set, get) => ({
      // Initial state
      tenantId: null,
      tenantInfo: null,

      // Actions
      setTenant: (info) =>
        set({
          tenantId: info.id,
          tenantInfo: info,
        }),

      clearTenant: () =>
        set({
          tenantId: null,
          tenantInfo: null,
        }),

      // Feature flag helper
      hasFeature: (feature) => {
        const { tenantInfo } = get();
        if (!tenantInfo) return false;
        return tenantInfo.features.includes(feature);
      },
    }),
    {
      name: 'tcy-tenant-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// Feature flags — keys MUST match the tenant.Features dictionary keys the API sends
// (see Tenant.SetDefaultFeatures on the backend), otherwise hasFeature() never matches.
export const FEATURES = {
  AI_DIAGNOSIS: 'AIDiagnosis',
  AI_QUICK_INTAKE: 'AIQuickIntake',
  MULTI_BRANCH: 'MultipleBranches',
  MULTIPLE_STAFF: 'MultipleStaff',
  ADVANCED_REPORTS: 'AdvancedReports',
  API_ACCESS: 'ApiAccess',
  WHITE_LABEL: 'WhiteLabel',
  DEVICE_SCANNER: 'DeviceScanner',
  PREDICTIVE_ANALYTICS: 'PredictiveAnalytics',
} as const;
